import React, { useEffect, useRef } from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { emphasizedScore, rampColor } from '../helper/alignmentGuidance';

interface AlignmentCornerLinesProps {
    box: RelBox | null;
    targetBox: RelBox;
    score: number;
    snapped: boolean;
}

// A particle only stores WHICH corner line it travels on and its timing — positions are derived
// from the CURRENT box/target every frame, so particles stay glued to the moving line instead of
// drifting off toward stale endpoints.
interface CornerParticle {
    corner: number;
    born: number;
    life: number;
    radius: number;
}

// Unit corner offsets: tl, tr, bl, br — index i maps IST corner i to SOLL corner i.
const CORNERS: ReadonlyArray<readonly [number, number]> = [
    [0, 0],
    [1, 0],
    [0, 1],
    [1, 1],
];

// Denser stream reads as a pull toward the target corners: more particles, spawned more often,
// slightly bigger and more opaque — the corner lines should feel like a current dragging the box
// into place, not a faint hint. Values are a first calibration; verify on-device.
const SPAWN_INTERVAL_MS = 50;
const MAX_PARTICLES = 96;
const LINE_WIDTH = 1.5;
const LINE_MAX_ALPHA = 0.7;
// Visibility ramps with the mean corner distance (fraction of the viewport diagonal): fully
// visible when far away, fully faded just before the snap so no short criss-cross "grid"
// flickers around the goal. Values are a first calibration; verify on-device.
const FADE_FULL_DIST = 0.15;
const FADE_ZERO_DIST = 0.04;

const cornerPoint = (b: RelBox, corner: number): { x: number; y: number } => ({
    x: b.x + CORNERS[corner][0] * b.w,
    y: b.y + CORNERS[corner][1] * b.h,
});

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

// Corner-to-corner guidance: four thin lines connect each corner of the tracked box with its
// corner of the target frame, with particles flowing along them toward the target — the most
// literal "move it from HERE to THERE" cue of the effect variants. Lines and particles fade out
// as the box approaches the target; on snap the layer goes dark and the frame glow takes over.
// prefers-reduced-motion keeps the static lines but skips the particles. Canvas + rAF so the
// animation never touches React rendering (same pattern as AlignmentParticles).
const AlignmentCornerLines = ({ box, targetBox, score, snapped }: AlignmentCornerLinesProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const boxRef = useRef(box);
    boxRef.current = box;
    const targetBoxRef = useRef(targetBox);
    targetBoxRef.current = targetBox;
    const scoreRef = useRef(score);
    scoreRef.current = score;
    const snappedRef = useRef(snapped);
    snappedRef.current = snapped;

    useEffect(() => {
        const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) {
            return undefined;
        }

        let cancelled = false;
        let rafHandle = 0;
        let lastSpawn = 0;
        const particles: CornerParticle[] = [];

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = canvas.clientWidth * dpr;
            canvas.height = canvas.clientHeight * dpr;
        };
        resize();
        window.addEventListener('resize', resize);

        const tick = (now: number) => {
            if (cancelled) {
                return;
            }
            rafHandle = window.requestAnimationFrame(tick);

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const currentBox = boxRef.current;
            if (!currentBox || snappedRef.current || !canvas.width || !canvas.height) {
                particles.length = 0;
                return;
            }

            const target = targetBoxRef.current;
            const diagonal = Math.hypot(canvas.width, canvas.height);
            let meanDist = 0;
            for (let corner = 0; corner < 4; corner++) {
                const from = cornerPoint(currentBox, corner);
                const to = cornerPoint(target, corner);
                meanDist += Math.hypot((to.x - from.x) * canvas.width, (to.y - from.y) * canvas.height);
            }
            meanDist /= 4 * diagonal;
            const visibility = Math.min(1, Math.max(0, (meanDist - FADE_ZERO_DIST) / (FADE_FULL_DIST - FADE_ZERO_DIST)));
            if (visibility <= 0) {
                return;
            }

            const color = rampColor(emphasizedScore(scoreRef.current));
            ctx.strokeStyle = color;
            ctx.lineWidth = LINE_WIDTH * (window.devicePixelRatio || 1);
            ctx.globalAlpha = visibility * LINE_MAX_ALPHA;
            for (let corner = 0; corner < 4; corner++) {
                const from = cornerPoint(currentBox, corner);
                const to = cornerPoint(target, corner);
                ctx.beginPath();
                ctx.moveTo(from.x * canvas.width, from.y * canvas.height);
                ctx.lineTo(to.x * canvas.width, to.y * canvas.height);
                ctx.stroke();
            }

            if (!reducedMotion) {
                if (now - lastSpawn >= SPAWN_INTERVAL_MS && particles.length + CORNERS.length <= MAX_PARTICLES) {
                    lastSpawn = now;
                    for (let corner = 0; corner < CORNERS.length; corner++) {
                        particles.push({ corner, born: now, life: 500 + Math.random() * 300, radius: 2.5 + Math.random() * 2 });
                    }
                }
                ctx.fillStyle = color;
                for (let i = particles.length - 1; i >= 0; i--) {
                    const p = particles[i];
                    const t = (now - p.born) / p.life;
                    if (t >= 1) {
                        particles.splice(i, 1);
                        continue;
                    }
                    const from = cornerPoint(currentBox, p.corner);
                    const to = cornerPoint(target, p.corner);
                    const eased = easeInOutCubic(t);
                    const x = (from.x + (to.x - from.x) * eased) * canvas.width;
                    const y = (from.y + (to.y - from.y) * eased) * canvas.height;
                    ctx.globalAlpha = visibility * Math.sin(Math.PI * t);
                    ctx.beginPath();
                    ctx.arc(x, y, p.radius * (window.devicePixelRatio || 1), 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            ctx.globalAlpha = 1;
        };

        rafHandle = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(rafHandle);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return <canvas ref={canvasRef} data-testid="alignment-corner-lines" className={'absolute inset-0 w-full h-full pointer-events-none'} aria-hidden={true} />;
};

export default AlignmentCornerLines;
